import {
    GroupIconPosition,
    PaymentGroup,
    PaymentGroupDataDef,
    PaymentGroupMeta,
    PaymentMethod,
    PaymentMethodMeta,
} from "@payment-ui/interfaces/Payment";
import { getIconCloudinaryUrl as getCloudinaryUrl } from "@payment-ui/helpers/Icon";
import { isAddNewCardPayment, isAddNewFoodCardPayment, isCardPaymentMethod } from "./Card";
import { WalletData } from "@payment-ui/interfaces";
import { generatebase64IconUrl } from "./Upi";
import { isInAppUpiPaymentMethod } from "./InAppUpi";

// constants
import { TEXTS } from "@payment-ui/constants/index";
import { PAYMENT_CODES } from "@swiggy-private/payment-types";

export const getPaymentMethods = <T extends PaymentGroupMeta, U extends PaymentMethod<PaymentMethodMeta>>(
    data: PaymentGroup<T, U>,
): U[] => {
    return data.paymentData?.paymentMethods ?? [];
};

export const isPaymentMethodSelected = (selectedMethod: string, paymentMethodId: string): boolean => {
    return selectedMethod === paymentMethodId;
};

export const getPaymentGroupName = (paymentData: PaymentGroupDataDef): string => {
    return paymentData.groupName.toUpperCase();
};

export const getPaymentGroupDisplayName = (paymentData: PaymentGroupDataDef): string => {
    return paymentData.displayName;
};

export const generatePaymentMethodId = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): string => {
    if (
        isCardPaymentMethod(paymentMethod) &&
        !isAddNewCardPayment(paymentMethod) &&
        !isAddNewFoodCardPayment(paymentMethod)
    ) {
        return `${paymentMethod.groupName}:${paymentMethod.name}:${paymentMethod.meta.cardReference}`;
    }
    if (isInAppUpiPaymentMethod(paymentMethod)) {
        return `${paymentMethod.groupName}:${paymentMethod.name}:${paymentMethod.meta?.referenceId}`;
    }
    return `${paymentMethod.groupName}:${paymentMethod.name}`;
};

export const isPaymentMethodEnabled = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): boolean => {
    return paymentMethod.enabled === true;
};

export const getPaymentMethodIconUrl = <T extends PaymentMethod<PaymentMethodMeta>>(
    paymentMethod: T,
    isThirdPartyIntentApp?: boolean,
): string => {
    const iconPath = paymentMethod.meta.iconUrl;

    if (!iconPath) {
        return "";
    }

    if (isThirdPartyIntentApp) {
        return generatebase64IconUrl(iconPath);
    } else {
        return getCloudinaryUrl(iconPath, {
            grayscale: !isPaymentMethodEnabled(paymentMethod),
        });
    }
};

export const getPaymentMethodName = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): string => {
    return paymentMethod.name;
};

export const getPaymentMethodDisplayName = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): string => {
    return paymentMethod.displayName;
};

export const getPaymentMethodDiscounts = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): string[] => {
    return paymentMethod?.discounts ?? [];
};

export const getPaymentMethodDiscount = <T extends PaymentMethod<PaymentMethodMeta>>(
    paymentMethod: T,
): string | undefined => {
    return paymentMethod?.discount;
};

export const getPaymentMethodNickName = <T extends PaymentMethod<PaymentMethodMeta>>(
    paymentMethod: T,
): string | null => {
    if (!isCardPaymentMethod(paymentMethod)) {
        return null;
    }
    return paymentMethod.meta.nickname ?? null;
};

export const getPaymentMethodPromoMsg = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): string => {
    return paymentMethod.meta.promoMsg ?? "";
};

export const getAmazonPayDelinkedMessage = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): string => {
    return paymentMethod.meta?.apayDelinkMethodMessage ?? "";
};

export const getPaymentMethodDisabledMsg = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): string => {
    return (
        (TEXTS.PAYMENT_METHOD_DISABLED_WALLET as Record<string, string>)[paymentMethod.name?.toUpperCase()] ??
        TEXTS.PAYMENT_METHOD_DISABLED_WALLET.DEFAULT
    );
};

export const getPaymentMethodDisableMsg = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): string => {
    return paymentMethod.meta.disableMessage ?? "";
};

export const getEnabledPaymentMethods = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethods: T[]): T[] => {
    return paymentMethods.filter(isPaymentMethodEnabled);
};

export const isCouponApplicableOnPaymentMethod = <T extends PaymentMethod<PaymentMethodMeta>>(
    paymentMethod: T,
): boolean => {
    return paymentMethod.couponApplicable === true;
};

export const getCouponApplicablePaymentMethods = <T extends PaymentMethod<PaymentMethodMeta>>(
    paymentMethods: T[],
): T[] => {
    return paymentMethods.filter(isCouponApplicableOnPaymentMethod);
};

export const getPaymentGroupBannerMessage = (paymentData: PaymentGroupDataDef): string | undefined => {
    return paymentData.meta?.bannerMessage ?? "";
};

// returning true when enabled field doesn't exist for backward compatibility
export const isPaymentGroupEnabled = (paymentData: PaymentGroupDataDef): boolean => paymentData.enabled ?? true;

export const getPaymentGroupDisableMessage = (paymentData: PaymentGroupDataDef): string =>
    paymentData.meta?.disableMessage ?? "";

export const getPaymentGroupIconUrl = (paymentData: PaymentGroupDataDef): string => {
    return paymentData.meta?.groupIconUrl || "";
};

export const getPaymentGroupIconPosition = (paymentData: PaymentGroupDataDef): GroupIconPosition => {
    return paymentData.meta?.groupIconPosition || GroupIconPosition.LEFT;
};

export const getPaymentGroupSubtext = (paymentData: PaymentGroupDataDef): string => {
    return paymentData.meta?.groupSubtext || "";
};

export const isFlipkartCoinsPaymentMethod = (paymentData: PaymentMethod<PaymentMethodMeta>): boolean => {
    return paymentData.paymentCode === PAYMENT_CODES.FLIPKART_SUPER_COIN;
};

export const isTwidRewardsPaymentMethod = (paymentData: PaymentMethod<PaymentMethodMeta>): boolean => {
    return paymentData.paymentCode === PAYMENT_CODES.TWID_REWARD;
};

export const canEnablePaymentMethod = (
    methodData: PaymentMethod<PaymentMethodMeta>,
    isGroupEnabled?: boolean,
): boolean => {
    return isGroupEnabled !== false && isPaymentMethodEnabled(methodData);
};

export const canEnableWalletMethod = (
    walletData: WalletData,
    methodData: PaymentMethod<PaymentMethodMeta>,
    isGroupEnabled?: boolean,
): boolean => {
    return walletData?.paymentMethodDisabled !== true && canEnablePaymentMethod(methodData, isGroupEnabled);
};

export const getPaymentAmount = (
    paymentAmount: string,
    paymentMethodData?: PaymentMethod<PaymentMethodMeta>,
): string => {
    return paymentMethodData?.payableAmount ? paymentMethodData?.payableAmount : paymentAmount;
};

/**
 * Checks if the payment method is MMT.
 * @param methodData The payment method data object.
 * @returns {boolean}
 */
export function isMMTPayment(methodData: { paymentCode: string }): boolean {
    return methodData.paymentCode.toUpperCase() === PAYMENT_CODES.MMT.toUpperCase();
}
